import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { handleBatchSendNochange, getAllClearingItems } from '../../../actions/ssmActions';
import { getBranchById, textualizationStatus } from '../../../utilities/common';
import { getBranchList } from '../../../actions/commonAction';
import PropTypes from 'prop-types';
import { makeStyles, useTheme } from "@material-ui/core";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from '@material-ui/core/Paper';
import IconButton from '@material-ui/core/IconButton';
import FirstPageIcon from '@material-ui/icons/FirstPage';
import KeyboardArrowLeft from '@material-ui/icons/KeyboardArrowLeft';
import KeyboardArrowRight from '@material-ui/icons/KeyboardArrowRight';
import LastPageIcon from '@material-ui/icons/LastPage';
import TableContainer from '@material-ui/core/TableContainer';
import TablePagination from '@material-ui/core/TablePagination';
import { useTranslation } from 'react-i18next';

// Fixed rows of the table per page
const useStylesFooter = makeStyles((theme) => ({
  root: {
    flexShrink: 0,
    marginLeft: theme.spacing(2.5),
    width: 300
  }
}));

function TablePaginationActions(props) {
  const { count, page, rowsPerPage, onChangePage } = props;
  const classes = useStylesFooter();
  const theme = useTheme();

  const handleFirstPageButtonClick = (event) => {
    onChangePage(event, 0);
  };  
  const handleBackButtonClick = (event) => {
    onChangePage(event, page - 1);
  };  
  const handleNextButtonClick = (event) => {
    onChangePage(event, page + 1);
  };  
  const handleLastPageButtonClick = (event) => {
    onChangePage(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <div className={classes.root}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </div>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onChangePage: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

// Display the table of batches 
const useStylesBody = makeStyles((theme) => ({
  selected: {
      "&.Mui-selected, &.Mui-selected:hover": {
          "& > .MuiTableCell-root": {
              backgroundColor: "rgba(255, 255, 255, 0.7) !important",
              color: "black !important"
          }
        }
  },
  hover: {
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.5) !important",
      color: "#fff !important",
    },
  },
  paper: {
      width: "100%"
  },
  container: {
      maxHeight: 600,
  },
  tableRowColor: { 
    background: '#212121' 
  },
  tableCellFormat: { 
    fontSize: '1.2rem', textAlign: 'center' 
  },
  tableCellContentFormat: { 
    textAlign: 'center' 
  }
}));

function DisplayGrid(props) {
    const dispatch = useDispatch();
    const classes = useStylesBody();
    const {t} = useTranslation('storageScan');

    const storageManagement = useSelector(state => state.storageScanManagement);   
    
    let rowsOrigin = storageManagement.clearingEvents.data;

    const [rows, setRows] = useState([]); 
    const [selectedID, setSelectedID] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [branches, setBranches] = useState([]);

    // Reverse batches information
    useEffect(() => {
      let rowsReverse = [];
      if(rowsOrigin) {
        for(let i = rowsOrigin.length - 1; i >= 0; i--) {
          rowsReverse.push(rowsOrigin[i]);
        }
      }
      setRows(rowsReverse);
    }, [rowsOrigin])
    // Get all branches 
    useEffect(() => {
      dispatch(getBranchList()).then((branches)=>{
          setBranches(branches);
      })
    }, [])
    
    // Transfer string to date in specified format
    function numDateTransfer(dateRaw) {
        const DateTimeOptions = {
            year: 'numeric', month: 'numeric', day: 'numeric',
            hour: 'numeric', minute: 'numeric', second: 'numeric',
            hour12: false
        };
        const dateNum = parseInt(dateRaw) * 1000;
        const date = new Intl.DateTimeFormat('en-CA', DateTimeOptions).format(dateNum);
        return date;
    }
    
    // Calculate rows per page
    const emptyRows = rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage);
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    let columnsHeader = [
      t('alert_date'),
      t('alert_status'),
      t('batch_send_no'),
      t('clearing_date'),
      t('clearing_status'),
      t('end_time'),
      t('expire_date'),
      t('items_count'),
      t('start_time'),
      t('storage_info_end'),
      t('warehouse')
    ]

    return (
        <Paper className={classes.paper}>
            <TableContainer className={classes.container}>
                <Table stickyHeader aria-label="sticky table">
                    <TableHead>
                        <TableRow className={classes.tableRowColor}>
                          {
                            columnsHeader.map((column) => {
                              return (
                                <TableCell
                                  key={column}
                                  className={classes.tableCellFormat}
                                >
                                  {column}
                                </TableCell>
                              )
                            })
                          }
                        </TableRow>
                    </TableHead>
                    <TableBody>
                    {(rowsPerPage > 0
                        ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : rows)
                        .map((row, index) => (
                        <TableRow 
                          hover
                          onClick={() => {
                              setSelectedID(row.id);
                              dispatch(handleBatchSendNochange(row.batch_send_no));
                              dispatch(getAllClearingItems(row.batch_send_no));
                              props.handleWarehouse(row.warehouse);
                          }}
                          style={index % 2? { background : "#424242", cursor: 'pointer' }:{ background : "#303030", cursor: 'pointer' }}
                          selected={selectedID === row.id}
                          classes={{
                              hover: classes.hover,
                              selected: classes.selected}}
                        >
                          { columns.map((column) => {
                              let item = '';
                              if(dateTimes.indexOf(column) !== -1) {
                                if(row[column]) {
                                  item = numDateTransfer(row[column]);
                                }
                              } else if(column === 'warehouse' && row.warehouse > 0 && branches.length > 0) {
                                item = getBranchById(row.warehouse).short_name;
                              } else if(column.indexOf('status') !== -1) {
                                item = textualizationStatus(row[column]);
                              } else {
                                item = row[column];
                              }
                              return (<TableCell
                                  className={classes.tableCellContentFormat}
                                >
                                  {item}
                                </TableCell>
                              );
                            })
                          }
                        </TableRow>
                    ))}
                    {emptyRows > 0 && (
                        <TableRow style={{ height: 53 * emptyRows }}>
                            <TableCell colSpan={6} />
                        </TableRow>
                    )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                colSpan={3}
                count={rows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                SelectProps={{
                    inputprops: { 'aria-label': 'rows per page' },
                    native: true,
                }}
                onChangePage={handleChangePage}
                onChangeRowsPerPage={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActions}
            />
        </Paper>
    );
}

export default DisplayGrid;

const columns = [
  'alert_date',
  'alert_status',
  'batch_send_no',
  'clearing_date',
  'clearing_status',
  'end_time',
  'expire_date',
  'items_count',
  'start_time',
  'storage_info_end',
  'warehouse'
]

const dateTimes = [
  'alert_date',
  'clearing_date',
  'end_time',
  'expire_date',
  'start_time'
]
